import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions, worksheetQuestions, exemptionQuestionsFromData, personalAllowancesQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const personalAllowancesForm = useRef();
  const personalAllowancesInitialQuestion = getInitialQuestions(personalAllowancesQuestion);
  const [paQuestion, setPAQuestion] = useState(personalAllowancesInitialQuestion);

  const worksheetForm = useRef();
  const worksheetInitialQuestions = getInitialQuestions(worksheetQuestions);
  const [wsQuestions, setWSQuestions] = useState(worksheetInitialQuestions);

  const exemptionsInitialQuestions = getInitialQuestions(exemptionQuestionsFromData);
  const [exemption, setExemption] = useState(data.exemption_reason || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding. (Line 3)
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderWorksheet = (isReviewStep) => (
    <div>
      <Text style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>
        Personal allowances (Line 1)
      </Text>
      <FormConsumer
        questions={paQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = paQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setPAQuestion(questions);
        }}
        ref={personalAllowancesForm}
      />
      <FormConsumer
        questions={wsQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = wsQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWSQuestions(questions);
        }}
        ref={worksheetForm}
      />
    </div>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <div>
      <Text style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>
        Exemption from withholding
      </Text>
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          Select an exemption status.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="top" mb={3}>
              <S.Input
                type="radio"
                value="ri_withholding"
                checked={exemption === 'ri_withholding'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemption(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column>
                <Text style={{ marginBottom: '10px' }}>
                  Select this option if you meet both of these conditions to claim exemption from Rhode Island withholding:
                </Text>
                <Text style={{ marginBottom: '10px' }}>
                  a) Last year I had a right to a refund of all Rhode Island income tax withheld because I had no tax liability AND
                </Text>
                <Text style={{ marginBottom: '5px' }}>
                  b) This year I expect a refund of all Rhode Island income tax because I expect to have no tax liability.
                </Text>
              </Flex>
            </Flex>
          </label>
          <label>
            <Flex alignItems="top">
              <S.Input
                type="radio"
                value="military_spouse"
                checked={exemption === 'military_spouse'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemption(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column>
                <Text style={{ marginBottom: '12px' }}>
                  Select this option if you are the spouse of a servicemember stationed in Rhode Island, your wages may be exempt under the Military Spouses
                  Residency Relief Act. If you meet both of these conditions below, you may claim exemption from Rhode Island withholding:
                </Text>
                <Text style={{ marginBottom: '12px' }}>
                  a) You moved to Rhode Island solely to be with your servicemember spouse in compliance with military orders sending the servicemember to Rhode Island AND
                </Text>
                b) You have the same non-Rhode Island domicile as your servicemember spouse.
              </Flex>
            </Flex>
          </label>
          {exemptionsError && exemption === '' && (
            <Flex justifyContent="flex-start" mt={1}>
              <Text color="red">This is a mandatory field.</Text>
            </Flex>
          )}
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderWorksheet()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (worksheetForm && worksheetForm.current) {
                worksheetForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            } else if (exemption === '') {
              err = true;
              setExemptionsError(true);
            } else {
              setExemptionsError(false);
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            let stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const fullName = stepOneData.employee_middle_name == '' ? `${stepOneData.employee_first_name} ${stepOneData.employee_last_name}` : `${stepOneData.employee_first_name} ${stepOneData.employee_middle_name} ${stepOneData.employee_last_name}`;
            stepOneData = { ...stepOneData, employee_full_name: fullName };
            let worksheetData;
            let exemptionTempData;
            let allAllowancesData;
            if (!isExemptFromWithholding) {
              worksheetData = paQuestion.concat(wsQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              exemptionTempData = '';
              const line_1_part_a = worksheetData.claim_dependents.includes('cannot_be_claimed_dependent') ? 1 : '';
              const line_1_part_b = worksheetData.claim_dependents.includes('claim_spouse_dependent') ? 1 : '';
              const line_1_part_c = worksheetData.num_dependents;
              const line_1_part_d = worksheetData.additional_allowances;
              const line_1_part_e_temp = (parseInt(line_1_part_a) || 0)
                  + (parseInt(line_1_part_b) || 0)
                  + (parseInt(line_1_part_c) || 0)
                  + (parseInt(line_1_part_d) || 0);
              const line_1_part_e = line_1_part_e_temp <= 10 ? line_1_part_e_temp : 10;
              allAllowancesData = {
                line_1_part_a: line_1_part_a.toString(),
                line_1_part_b: line_1_part_b.toString(),
                line_1_part_c: line_1_part_c.toString(),
                line_1_part_d: line_1_part_d.toString(),
                line_1_part_e: line_1_part_e.toString(),
              };
            } else {
              exemptionTempData = exemption;
              worksheetData = resetInitialQuestions(wsQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              allAllowancesData = {
                line_1_part_a: '',
                line_1_part_b: '',
                line_1_part_c: '',
                line_1_part_d: '',
                line_1_part_e: '',
              };
            }
            const exemptionData = { exemption_reason: exemptionTempData, is_exempt: isExemptFromWithholding };
            const data = {
              ...stepOneData, ...worksheetData, ...allAllowancesData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderWorksheet(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LegacyForm;
